/**
 * @module HashConflictResolver
 * @purpose FR10 hash-conflict resolution — warns of diverging hash values across artefacts,
 *   shows original vs conflicting values side-by-side, and lets the analyst choose the
 *   authoritative value. Once a choice is persisted the component renders the chosen value
 *   as authoritative instead of the warning/choice UI.
 * @depends types/reasoning, react-i18next
 * @dependedOnBy FactPackPanel (hash group section)
 *
 * @rules
 * - Performance: pure display — all data passed via props; local state for choice and error only
 * - State: local useState for chosen resolution and inline error state
 * - Errors: async handleChoose catches rejection and shows an inline error span
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { FileHashEntry, HashResolution } from '../../../../../types/reasoning'

interface HashConflictResolverProps {
  basename: string
  entry: FileHashEntry
  onResolveHash?: (resolution: HashResolution) => Promise<void>
}

type ConflictChoice = 'original' | 'conflicting' | null

const WARNING_BOX: React.CSSProperties = {
  marginTop: '8px',
  padding: '8px 10px',
  borderRadius: '5px',
  border: '1px solid rgba(220, 150, 50, 0.35)',
  background: 'rgba(220, 150, 50, 0.06)',
}

const WARNING_LABEL: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.68rem',
  color: 'rgba(220, 150, 50, 0.85)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: '6px',
  display: 'block',
}

const RESOLVED_BOX: React.CSSProperties = {
  marginTop: '8px',
  padding: '8px 10px',
  borderRadius: '5px',
  border: '1px solid var(--border)',
  background: 'rgba(0, 0, 0, 0.03)',
}

const RESOLVED_LABEL: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.68rem',
  color: 'var(--text-dim)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: '6px',
  display: 'block',
}

const RESOLVED_FOOTER: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.62rem',
  color: 'var(--text-muted)',
  marginTop: '6px',
  display: 'block',
}

const SIDE_BY_SIDE: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '8px',
  marginBottom: '8px',
}

const VALUE_CELL: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.66rem',
  color: 'var(--text-dim)',
  wordBreak: 'break-all',
}

const CELL_LABEL: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.62rem',
  color: 'var(--text-muted)',
  marginBottom: '2px',
}

const RESOLVE_BTN: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.64rem',
  padding: '3px 8px',
  borderRadius: '4px',
  border: '1px solid var(--border)',
  background: 'transparent',
  color: 'var(--text-dim)',
  cursor: 'pointer',
  marginRight: '6px',
}

const RESOLVE_BTN_ACTIVE: React.CSSProperties = {
  ...RESOLVE_BTN,
  background: 'var(--accent)',
  color: 'var(--btn-primary-text, #fff)',
  border: 'none',
}

const INLINE_ERROR: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.62rem',
  color: 'var(--text-muted)',
  marginTop: '5px',
  display: 'block',
}

const CELL_LABEL_MT: React.CSSProperties = { ...CELL_LABEL, marginTop: '5px' }

interface ChoiceButtonProps {
  active: boolean
  label: string
  onClick: () => void
}

function ChoiceButton({ active, label, onClick }: ChoiceButtonProps): React.ReactElement {
  return (
    <button
      type="button"
      style={active ? RESOLVE_BTN_ACTIVE : RESOLVE_BTN}
      onClick={onClick}
      aria-pressed={active}
    >
      {label}
    </button>
  )
}

function buildResolution(
  basename: string,
  choice: ConflictChoice,
  entry: FileHashEntry,
): HashResolution | null {
  if (choice === null) return null
  if (choice === 'original') {
    return { basename, chosenMd5: entry.md5, chosenSha256: entry.sha256 }
  }
  const conflictMd5 = entry.conflicting_md5?.[0] ?? null
  const conflictSha256 = entry.conflicting_sha256?.[0] ?? null
  return { basename, chosenMd5: conflictMd5, chosenSha256: conflictSha256 }
}

interface ResolvedViewProps {
  entry: FileHashEntry
}

function ResolvedView({ entry }: ResolvedViewProps): React.ReactElement {
  const { t } = useTranslation('analysis')
  const resolvedAtDisplay =
    entry.resolved_at !== undefined
      ? new Date(entry.resolved_at).toLocaleString()
      : '—'
  return (
    <div style={RESOLVED_BOX} role="status">
      <span style={RESOLVED_LABEL}>
        {t('reasoning.briefingBoard.factPack.hashes.resolvedAuthoritative' as never)}
      </span>
      <div>
        <div style={CELL_LABEL}>{t('reasoning.briefingBoard.factPack.hashes.originalMd5' as never)}</div>
        <div style={VALUE_CELL}>{entry.chosen_md5 ?? '—'}</div>
        <div style={CELL_LABEL_MT}>{t('reasoning.briefingBoard.factPack.hashes.originalSha256' as never)}</div>
        <div style={VALUE_CELL}>{entry.chosen_sha256 ?? '—'}</div>
      </div>
      <span style={RESOLVED_FOOTER}>
        {t('reasoning.briefingBoard.factPack.hashes.resolvedAt' as never, { at: resolvedAtDisplay })}
      </span>
    </div>
  )
}

interface UnresolvedViewProps {
  basename: string
  entry: FileHashEntry
  onResolveHash?: ((resolution: HashResolution) => Promise<void>) | undefined
}

function UnresolvedView({ basename, entry, onResolveHash }: UnresolvedViewProps): React.ReactElement {
  const { t } = useTranslation('analysis')
  const [choice, setChoice] = useState<ConflictChoice>(null)
  const [error, setError] = useState<string | null>(null)

  const conflictMd5 = entry.conflicting_md5?.[0] ?? null
  const conflictSha256 = entry.conflicting_sha256?.[0] ?? null

  async function handleChoose(chosen: 'original' | 'conflicting'): Promise<void> {
    const resolution = buildResolution(basename, chosen, entry)
    if (resolution === null) return
    setChoice(chosen)
    setError(null)
    try {
      await onResolveHash?.(resolution)
    } catch {
      setError(t('reasoning.briefingBoard.factPack.hashes.resolveError' as never))
    }
  }

  return (
    <div style={WARNING_BOX} role="alert" aria-label={t('reasoning.briefingBoard.factPack.hashes.conflictWarning' as never)}>
      <span style={WARNING_LABEL}>
        {t('reasoning.briefingBoard.factPack.hashes.conflictWarning' as never)}
      </span>
      <div style={SIDE_BY_SIDE}>
        <div>
          <div style={CELL_LABEL}>{t('reasoning.briefingBoard.factPack.hashes.originalMd5' as never)}</div>
          <div style={VALUE_CELL}>{entry.md5 ?? '—'}</div>
          <div style={CELL_LABEL_MT}>{t('reasoning.briefingBoard.factPack.hashes.originalSha256' as never)}</div>
          <div style={VALUE_CELL}>{entry.sha256 ?? '—'}</div>
        </div>
        <div>
          <div style={CELL_LABEL}>{t('reasoning.briefingBoard.factPack.hashes.conflictingMd5' as never)}</div>
          <div style={VALUE_CELL}>{conflictMd5 ?? '—'}</div>
          <div style={CELL_LABEL_MT}>{t('reasoning.briefingBoard.factPack.hashes.conflictingSha256' as never)}</div>
          <div style={VALUE_CELL}>{conflictSha256 ?? '—'}</div>
        </div>
      </div>
      <div>
        <ChoiceButton
          active={choice === 'original'}
          label={t('reasoning.briefingBoard.factPack.hashes.originalValues' as never)}
          onClick={() => { void handleChoose('original') }}
        />
        <ChoiceButton
          active={choice === 'conflicting'}
          label={t('reasoning.briefingBoard.factPack.hashes.conflictingValues' as never)}
          onClick={() => { void handleChoose('conflicting') }}
        />
      </div>
      {error !== null && (
        <span style={INLINE_ERROR} role="alert">
          {error}
        </span>
      )}
    </div>
  )
}

/**
 * Dispatches between resolved and unresolved hash-conflict states.
 *
 * @behaviour When entry.resolved is true, delegates to ResolvedView (authoritative value panel).
 *   When hash_conflict is true and not yet resolved, delegates to UnresolvedView (warning + choice UI).
 *   When hash_conflict is false or absent, renders nothing.
 * @errors Unresolved branch catches onResolveHash rejection and shows inline error.
 */
export function HashConflictResolver({
  basename,
  entry,
  onResolveHash,
}: HashConflictResolverProps): React.ReactElement {
  if (entry.hash_conflict !== true) return <></>
  if (entry.resolved === true) return <ResolvedView entry={entry} />
  return <UnresolvedView basename={basename} entry={entry} onResolveHash={onResolveHash} />
}
